import { useState } from 'react';

import type {
  TStaffHandover,
  TStaffHoldKind,
  TStaffTransitionIntent,
  TStaffTransitionReport,
} from '@/services/staff';

import { isTransitionClear, toTransitionPayload } from '@/utils/staffTransition';

import { useApplyStaffTransition } from './useApplyStaffTransition';
import { usePreviewStaffTransition } from './usePreviewStaffTransition';

const useStaffTransitionGuard = () => {
  const preview = usePreviewStaffTransition();
  const apply = useApplyStaffTransition();

  const [intent, setIntent] = useState<TStaffTransitionIntent | null>(null);
  const [handover, setHandover] = useState<TStaffHandover>({});
  const [report, setReport] = useState<TStaffTransitionReport | null>(null);

  const isOpen = intent !== null;
  const isChecking = !isOpen && (preview.isPending || apply.isPending);

  const guard = (next: TStaffTransitionIntent) => {
    preview.reset();
    apply.reset();
    setHandover({});
    setReport(null);

    const onOpen = () => {
      setIntent(next);
    };

    const onPreviewed = (result: TStaffTransitionReport) => {
      setReport(result);

      if (!isTransitionClear(result)) {
        onOpen();
        return;
      }

      apply.mutate(toTransitionPayload(next, {}, false), { onError: onOpen });
    };

    preview.mutate(toTransitionPayload(next, {}, true), { onSuccess: onPreviewed, onError: onOpen });
  };

  const choose = (kind: TStaffHoldKind, strategy: string) => {
    if (intent === null) {
      return;
    }

    const nextHandover = { ...handover, [kind]: strategy };

    apply.reset();
    setHandover(nextHandover);
    preview.mutate(toTransitionPayload(intent, nextHandover, true), { onSuccess: setReport });
  };

  const confirm = () => {
    if (intent === null) {
      return;
    }

    apply.mutate(toTransitionPayload(intent, handover, false));
  };

  const close = () => {
    setIntent(null);
    setHandover({});
    setReport(null);
    preview.reset();
    apply.reset();
  };

  return {
    intent,
    handover,
    report,
    outcome: apply.data ?? null,
    previewError: preview.error,
    applyError: apply.error,
    isOpen,
    isChecking,
    isPreviewing: preview.isPending,
    isApplying: apply.isPending,
    guard,
    choose,
    confirm,
    close,
  };
};

type TStaffTransitionGuard = ReturnType<typeof useStaffTransitionGuard>;

export type {
  TStaffTransitionGuard,
};
export {
  useStaffTransitionGuard,
};
